<template>
	<div class="row">

		<div class="col-md-12 mb-5">
			<p class="step-title">
				{{ $t('account/bookings.Informations') }}
			</p>
		</div>

		<div class="form-group col-md-6">
			<input-text
				@input="checkInput()"
				:label="$t('account/bookings.Registration towing')"
				v-model="towing_registration"
			/>

			<booking-alert
				class="mt-2"
				v-if="errors.includes('towing_registration')"
			>
				{{ $t('account/bookings.This field is required') }}
			</booking-alert>
		</div>

		<div class="form-group col-md-6">
			<input-text
				@input="checkInput()"
				:label="$t('account/bookings.Registration trailer')"
				v-model="trailer_registration"
			/>

			<booking-alert
				class="mt-2"
				v-if="errors.includes('trailer_registration')"
			>
				{{ $t('account/bookings.This field is required') }}
			</booking-alert>
		</div>

		<div
			v-if="['customs-sivep', 'customs'].includes(booking.service)"
			class="form-group col-md-6"
		>
			<input-select
				@input="checkInput()"
				:options="incoterms"
				:clearable="false"
				:name="$t('account/bookings.Incoterm')"
				v-model="incoterm"
			/>

			<booking-alert
				class="mt-2"
				v-if="errors.includes('incoterm')"
			>
				{{ $t('account/bookings.This field is required') }}
			</booking-alert>
		</div>

		<div
			v-if="['customs-sivep', 'customs'].includes(booking.service)"
			class="form-group col-md-6"
		>

			<form-label>
				{{ $t('account/bookings.Transport cost') }}
			</form-label>

			<div class="input-group">

				<input
					type="text"
					class="form-control"
					@input="checkInput()"
					v-model="transport_cost"
				/>

				<div class="input-group-append">
					<button
						class="btn dropdown-toggle"
						type="button"
						data-toggle="dropdown"
						aria-haspopup="true"
						aria-expanded="false"
					>
						{{ currency }}
					</button>

					<div class="dropdown-menu">
						<span
							@click="currency = item; transport_cost = transport_cost"
							:key="'currency-' + item"
							v-for="item in currencies"
							class="dropdown-item"
						>
							{{ item }}
						</span>
					</div>
				</div>

			</div>

			<booking-alert
				class="mt-2"
				v-if="errors.includes('transport_cost')"
			>
				{{ $t('account/bookings.This field is required') }}
			</booking-alert>
		</div>

		<div class="form-group col-md-6">
			<input-date
				@input="checkInput()"
				:from-today="true"
				:label="$t('account/bookings.Crossing date')"
				v-model="crossing_date"
				showSelectToday
			>
				<template #footer>
					<div class="d-flex align-items-center justify-content-center">
						<label>
							<input type="checkbox" v-model="crossing_date_use_today" @change="setToday" />
							{{ $t('form.Today') }}
						</label>
					</div>
				</template>
			</input-date>	

			<booking-alert
				class="mt-2"
				v-if="errors.includes('crossing_date')"
			>
				{{ $t('account/bookings.This field is required') }}
			</booking-alert>
		</div>

		<div class="form-group col-md-6">
			<input-select
				@input="checkInput()"
				:name="$t('account/bookings.Customs type')"
				:clearable="false"
				v-model="customs_type"
				:options="customs"
			/>

			<booking-alert
				class="mt-2"
				v-if="errors.includes('customs_type')"
			>
				{{ $t('account/bookings.This field is required') }}
			</booking-alert>
		</div>

		<div class="form-group col-md-6">
			<input-select
				@input="checkInput()"
				:name="$t('account/bookings.Crossing place')"
				:clearable="false"
				v-model="crossing_place"
				:reduce="o => o.value"
				:options="places"
			>

				<template #option="{ option }">
					{{ option.label }}

					<span
						v-if="['calais-port', 'calais-shuttle'].includes(option.value)"
						class="badge bg-light text-dark small px-3 py-2 ml-1"
					>
						<template v-if="option.value == 'calais-port'">
							Ferry
						</template>

						<template v-if="option.value == 'calais-shuttle'">
							Train
						</template>
					</span>

				</template>

			</input-select>

			<booking-alert
				class="mt-2"
				v-if="errors.includes('crossing_place')"
			>
				{{ $t('account/bookings.This field is required') }}
			</booking-alert>
		</div>

		<div
			v-if="booking.service != 'parking'"
			class="form-group col-md-6"
		>
			<input-select
				@input="checkInput()"
				:name="$t('account/bookings.Type')"
				:clearable="false"
				v-model="type"
				:reduce="o => o.key"
				:options="types"
			/>

			<booking-alert
				class="mt-2"
				v-if="errors.includes('type')"
			>
				{{ $t('account/bookings.This field is required') }}
			</booking-alert>
		</div>

		<transition name="slide-fade">

			<div
				v-if="[
					'moving',
					'personal_effects',
					'military_equipment'
				].includes(type)"
				class="col-md-12 p-5 alert alert-light mb-3"
			>

				<div class="h4 mb-4">
					<i class="bi bi-info-circle mr-3"></i>
					{{ $t('account/bookings.Information') }}
				</div>

				<p>
					{{ $t('account/bookings.' + type + ' text') }}
				</p>
			</div>

		</transition>

		<transition name="slide-fade">

			<div
				v-if="type == 'animals'"
				class="form-group col-sm-6"
			>
				<input-select
					@input="checkInput()"
					:name="$t('account/bookings.Animals type')"
					v-model="animals"
					:options="animalsTypes"
					:reduce="o => o.value"
					:clearable="false"
				/>

				<booking-alert
					class="mt-2"
					v-if="errors.includes('animals')"
				>
					{{ $t('account/bookings.This field is required') }}
				</booking-alert>
			</div>

		</transition>

		<div class="form-group col-md-6">
			<input-text
				@input="checkInput()"
				:label="$t('account/bookings.Reference')"
				v-model="reference"
			/>

			<booking-alert
				class="mt-2"
				v-if="errors.includes('reference')"
			>
				{{ $t('account/bookings.This field is required') }}
			</booking-alert>
		</div>

		<div class="form-group col-md-12">
			<input-textarea
				@input="checkInput()"
				:label="$t('account/bookings.Notes')"
				v-model="notes"
			/>
		</div>

	</div>
</template>

<script>

import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'

export default {

	data() {
		return {

			currency: '€',

			errors: [],
			customs: [
				'Transit',
				'Import',
				'Export',
				'ATA',
				'TIR'
			],

			currencies: [
				'€',
				'$',
				'£'
			],

			incoterms: [
				'EXW',
				'FCA',
				'FAS',
				'FOB',
				'CFR',
				'CIF',
				'CPT',
				'CIP',
				'DAP',
				'DPU',
				'DDP',
			],

			animalsTypes: [
				{ label: this.$t('app.animals.horses'), value: "horses" },
				{ label: this.$t('app.animals.dogs_cats'), value: "dogs_cats" },
				{ label: this.$t('app.animals.chicks'), value: "chicks" },
			],
		}
	},

	computed: {
		...mapState({
			booking: state => state.booking
		}),

		...mapGetters('booking', [
			'mandate'
		]),


		types() {
			return _.filter(this.booking.types, type => {

				if (this.site == 'customs-department') {
					return type.sites.includes('customs-department')
				}

				if (type.sites.includes('customs-department')) {
					return type.sites.length > 1
				}

				if (this.booking.service == 'customs-sivep') {
					return ![
						'customs-cts',
						'customs'
					].includes(type.key)
				}

				return true;

			})
		},

		places() {
			return _.filter(this.booking.places, place => {
				// Services rules
				if (this.booking.service == 'parking') {
					return ['calais-truck-stop', 'dunkirk-port'].includes(place.value);
				} else {
					return !['calais-truck-stop'].includes(place.value);
				}
			});
		},

		towing_registration: {
			get() { return this.booking.towing_registration },
			set(value) { this.$store.commit('booking/setTowingRegistration', value) }
		},

		trailer_registration: {
			get() { return this.booking.trailer_registration },
			set(value) { this.$store.commit('booking/setTrailerRegistration', value) }
		},

		customs_type: {
			get() { return this.booking.customs_type },
			set(value) { this.$store.commit('booking/setCustomsType', value) }
		},

		site: {
			get() { return this.booking.site },
			set(value) { this.$store.commit('booking/setSite', value) }
		},

		type: {
			get() { return this.booking.type },
			set(value) { this.$store.commit('booking/setType', value) }
		},

		reference: {
			get() { return this.booking.reference },
			set(value) { this.$store.commit('booking/setReference', value) }
		},

		notes: {
			get() { return this.booking.notes },
			set(value) { this.$store.commit('booking/setNotes', value) }
		},

		incoterm: {
			get() { return this.booking.data.incoterm },
			set(value) {
				this.$store.commit('booking/setData', {
					key: 'incoterm',
					value: value
				})
			}
		},

		transport_cost: {
			get() { return parseInt(this.booking.data.transport_cost) || 0 },
			set(value) {
				this.$store.commit('booking/setData', {
					key: 'transport_cost',
					value: value + ' ' + this.currency
				})
			}
		},

		crossing_date: {
			get() { return this.booking.data.crossing_date },
			set(value) {
				this.$store.commit('booking/setData', {
					key: 'crossing_date',
					value: value
				})
			}
		},

		crossing_date_use_today: {
			get() { return this.booking.data.crossing_date_use_today },
			set(value) {
				this.$store.commit('booking/setData', {
					key: 'crossing_date_use_today',
					value: value
				})
			}
		},

		crossing_place: {
			get() { return this.booking.data.crossing_place },
			set(value) {
				this.$store.commit('booking/setData', {
					key: 'crossing_place',
					value: value
				})

				if (!this.site) {
					this.site = value
				}
			}
		},

		animals: {
			get() { return this.booking.data.animals },
			set(value) {
				this.$store.commit('booking/setData', {
					key: 'animals',
					value: value
				})
			}
		},

		


	},

	methods: {

		checkInput: _.debounce(function () {
			if (this.errors.length) {
				this.checkComplete()
			}
		}, 200),

		checkComplete() {

			this.errors = []

			var keys = [
				'towing_registration',
				'trailer_registration',
				'type',
				'customs_type',
				'reference',
				'crossing_date',
				'crossing_place',
			]

			if (['customs', 'customs-sivep'].includes(this.booking.service)) {
				keys.push('incoterm')
				keys.push('transport_cost')
			}

			if (this.booking.type == 'animals') {
				keys.push('animals')
			}

			_.map(keys, key => {

				var value = this[key]

				if (_.isArray(value) ? !value.length : !value) {
					this.errors.push(key)
				}

			})

			return this.errors.length
				? false
				: true

		},

		setToday() {
			if(this.crossing_date_use_today) {
				this.crossing_date = new Date()
			} else {
				this.crossing_date = null
			}
		},

	}

}

</script>


<style lang="scss" scoped>
.input-group>.input-group-append>.btn {
	border-color: #E2E8F0;
}
</style>